import React, { useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel_HRM3040_R01, getHRM3040_R01List, getHRM3040_R01PreviewList, patchHRM3040_R01 } from "./Api_HRM3040_R01";
import {
    HRM3040_R01ServiceAllowDto, HRM3040_R01PositionAllowDto, HRM3040_R01PreviewDto,
    defaultHRM3040_R01ServiceAllowDto, defaultHRM3040_R01PositionAllowDto,
} from "./Dto_HRM3040_R01";
import { useHRM3040_R01Store } from "./Store_HRM3040_R01";
import { HRM3040_R01GridType, HRM3040_R01_OPEN_END_DT, validateHRM3040_R01SaveData } from "./Util_HRM3040_R01";

/** 세 표의 참조와 화면 하단 메시지 변경 함수이다. */
interface Props {
    serviceGridRef: React.RefObject<AgGridReact<HRM3040_R01ServiceAllowDto> | null>; // 근속 표의 편집 확정·스크롤 참조
    positionGridRef: React.RefObject<AgGridReact<HRM3040_R01PositionAllowDto> | null>; // 직책 표의 편집 확정·스크롤 참조
    previewGridRef: React.RefObject<AgGridReact<HRM3040_R01PreviewDto> | null>; // 미리보기 스크롤 참조
    setMessage: (message: string) => void; // 하단 결과 안내 변경
}
/** 조회 부가 옵션이다. */
type FetchOptions = {
    messageMode?: 'show' | 'none'; // none이면 조회 완료·빈 목록 알림 생략
};

/** 필터 조회·등록·삭제 표시·한 번의 저장·Excel·초기화 함수를 구성해 반환한다. 자동 조회와 빈 신규 행 생성은 하지 않는다. */
export const HRM3040_R01Content = ({ serviceGridRef, positionGridRef, previewGridRef, setMessage }: Props) => {
    // 공통 UI Hook에서 변경 확인창 상태 변경 함수를 읽는다.
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    // 공통 로딩 표시 변경 함수를 읽는다.
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // true이면 행 클릭을 선택 처리한다. +/- 직후 같은 클릭은 공통 행 선택 훅에서 한 번 무시한다.
    const [clickable, setClickable] = useState(true);
    // 화면별 액션바 권한을 연결하며 업무 데이터 조회는 실행하지 않는다.
    useFetchActionbarAuth("HRM3040_R01");

    /** 입력 중인 두 표의 셀 값을 Store에 확정한다. 반환값은 없다. */
    const stopEditing = useCallback(() => {
        // ?.는 아직 그리드가 생성되지 않았다면 호출을 건너뛴다.
        serviceGridRef.current?.api.stopEditing();
        positionGridRef.current?.api.stopEditing();
    }, [serviceGridRef, positionGridRef]);

    /** 두 기준표에 신규·수정·삭제가 하나라도 있으면 true를 반환한다. */
    const hasChanges = useCallback((): boolean => {
        const state = useHRM3040_R01Store.getState(); // stopEditing 이후의 최신 편집 상태
        return state.deletedHRM3040_R01ServiceList.length > 0 || state.deletedHRM3040_R01PositionList.length > 0
            || state.HRM3040_R01ServiceList.some(row => row.inserted || row.isModified)
            || state.HRM3040_R01PositionList.some(row => row.inserted || row.isModified);
    }, []);

    /** 공통 확인창을 닫고 속성을 비운다. */
    const closeConfirmModal = useCallback(() => {
        setConfirmModalVisible(false);
        setConfirmModalProps(undefined);
    }, [setConfirmModalVisible, setConfirmModalProps]);

    /** 변경이 있으면 HRM2120과 같은 공통 확인창을 연다. 확인한 뒤에만 전달된 동작을 실행한다. */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) { onConfirm(); return; }
        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인', cancelText: '취소',
            /** 확인창을 닫고 사용자가 승인한 조회 또는 초기화를 수행한다. */
            onConfirm: () => { closeConfirmModal(); onConfirm(); },
            onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, closeConfirmModal, setConfirmModalProps, setConfirmModalVisible]);

    /** 최신 필터로 기준 두 표와 미리보기를 함께 조회한다. 실패한 응답 쪽의 기존 목록은 유지한다. */
    const executeDataFetch = useCallback(async (fetchType: 'search' | 'refetch', options?: FetchOptions) => {
        const filter = { ...useHRM3040_R01Store.getState().HRM3040_R01Filter }; // 요청 도중 변경되지 않을 조건 복사본
        if (!filter.baseDt) { showWarning('기준일자를 확인해주세요.'); return; }
        setLoadingSpinner(true);
        try {
            // 각 실패를 따로 처리해 한 요청이 네트워크 오류여도 성공한 다른 결과는 반영한다. 오류 안내는 공통 인터셉터가 처리한다.
            const [ruleResponse, previewResponse] = await Promise.all([
                getHRM3040_R01List(fetchType, filter).catch(() => undefined),
                getHRM3040_R01PreviewList(fetchType, filter).catch(() => undefined),
            ]);
            const state = useHRM3040_R01Store.getState(); // 조회 완료 시점의 목록 변경 함수
            const rules = ruleResponse?.data.successFlag ? ruleResponse.data.data : undefined; // 성공한 기준 응답만 사용
            const preview = previewResponse?.data.successFlag ? previewResponse.data.data : undefined; // 성공한 미리보기만 사용
            if (rules) {
                state.setHRM3040_R01ServiceList(rules.hrmServiceAllowDtoList);
                state.setHRM3040_R01PositionList(rules.hrmPositionAllowDtoList);
                state.setOriginalHRM3040_R01ServiceList(rules.hrmServiceAllowDtoList);
                state.setOriginalHRM3040_R01PositionList(rules.hrmPositionAllowDtoList);
                state.initSelectedHRM3040_R01ServiceList();
                state.initSelectedHRM3040_R01PositionList();
                state.removeDeletedHRM3040_R01ServiceList();
                state.removeDeletedHRM3040_R01PositionList();
                if (fetchType === 'search') { state.setServiceScrollTop(null); state.setPositionScrollTop(null); }
                if (rules.hrmServiceAllowDtoList.length) serviceGridRef.current?.api.ensureIndexVisible(0, 'top');
                if (rules.hrmPositionAllowDtoList.length) positionGridRef.current?.api.ensureIndexVisible(0, 'top');
            }
            if (preview) {
                state.setHRM3040_R01PreviewList(preview);
                if (fetchType === 'search') state.setPreviewScrollTop(null);
                if (preview.length) previewGridRef.current?.api.ensureIndexVisible(0, 'top');
            }
            // 두 응답 모두 실패하면 직전 조회 조건도 유지한다.
            if (rules || preview) state.setHRM3040_R01PastFilter(filter);
            if (rules && preview && options?.messageMode !== 'none') {
                const serviceCount = rules.hrmServiceAllowDtoList.length; // 조회한 근속 행 수
                const positionCount = rules.hrmPositionAllowDtoList.length; // 조회한 직책 행 수
                if (serviceCount + positionCount + preview.length === 0) showWarning('데이터가 없습니다.');
                else ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (근속 ${serviceCount}건 · 직책 ${positionCount}건 · 미리보기 ${preview.length}건)`);
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, serviceGridRef, positionGridRef, previewGridRef, setMessage]);

    /** 조회 버튼은 편집 중인 값을 확정한 뒤 변경 폐기 확인을 거쳐 조회한다. */
    const fetchData = useCallback(() => {
        stopEditing();
        confirmDiscard(() => { void executeDataFetch('search'); });
    }, [stopEditing, confirmDiscard, executeDataFetch]);

    /** 마지막 근속 행의 미만 년수에서 시작하는 신규 근속 기준을 반환한다. */
    const defaultHRM3040_R01ServiceRow = useCallback((): HRM3040_R01ServiceAllowDto => {
        const state = useHRM3040_R01Store.getState(); // 마지막 조회 기준일과 현재 근속 목록
        const last = state.HRM3040_R01ServiceList[state.HRM3040_R01ServiceList.length - 1]; // 마지막 업무 행
        const fromYear = last?.toYear ?? 0; // 기존 마지막 구간 끝. 목록이 없으면 0년
        return { ...defaultHRM3040_R01ServiceAllowDto, startDt: state.HRM3040_R01PastFilter.baseDt,
            endDt: HRM3040_R01_OPEN_END_DT, fromYear, toYear: fromYear + 1, allowAmt: 0, useYn: 'Y', inserted: true };
    }, []);

    /** 마지막 조회 기준일에서 시작하는 직책 미선택 신규 기준을 반환한다. */
    const defaultHRM3040_R01PositionRow = useCallback((): HRM3040_R01PositionAllowDto => {
        const state = useHRM3040_R01Store.getState(); // 직전 조회 기준일
        return { ...defaultHRM3040_R01PositionAllowDto, startDt: state.HRM3040_R01PastFilter.baseDt,
            endDt: HRM3040_R01_OPEN_END_DT, allowAmt: 0, useYn: 'Y', inserted: true };
    }, []);

    /** 등록은 마지막 클릭한 그리드 끝에, index +는 클릭 행 뒤에 신규 행을 만든다. 조회 전에는 막는다. */
    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number, gridType?: HRM3040_R01GridType) => {
        const state = useHRM3040_R01Store.getState(); // 렌더 값이 아닌 클릭 시점의 대상 그리드와 최신 목록
        if (!state.HRM3040_R01PastFilter.baseDt) { showWarning('먼저 조회하세요.'); return; }
        // ??는 + 버튼이 전달한 대상을 우선하며 없으면 현재 Store의 대상을 사용한다.
        // activeGridType을 이 함수 의존성에 넣지 않아 대상 전환 중 + 버튼이 재생성되어 클릭을 잃지 않게 한다(HRM3110).
        const target = gridType ?? state.activeGridType; // 추가할 표
        if (rowIndex !== undefined) setClickable(false);
        if (target === 'service') {
            const newRow = defaultHRM3040_R01ServiceRow(); // 새 근속 기준 전체 값
            if (rowIndex === undefined) state.insertHRM3040_R01ServiceList([newRow]);
            else InsertRowHandler({ event, dataList: state.HRM3040_R01ServiceList, rowData: state.HRM3040_R01ServiceList[rowIndex],
                insertRow: state.insertHRM3040_R01ServiceList, defaultRowData: newRow, setClickable });
        } else {
            const newRow = defaultHRM3040_R01PositionRow(); // 새 직책 기준 전체 값
            if (rowIndex === undefined) state.insertHRM3040_R01PositionList([newRow]);
            else InsertRowHandler({ event, dataList: state.HRM3040_R01PositionList, rowData: state.HRM3040_R01PositionList[rowIndex],
                insertRow: state.insertHRM3040_R01PositionList, defaultRowData: newRow, setClickable });
        }
    }, [defaultHRM3040_R01ServiceRow, defaultHRM3040_R01PositionRow]);

    /** 대상 표에 D 표시가 있으면 전체 복구하고, 없으면 선택 신규 행은 제거·저장 행은 D 표시한다. */
    const handleDeleteClick = useCallback(() => {
        stopEditing();
        const state = useHRM3040_R01Store.getState(); // 삭제 버튼 시점의 대상·목록
        const isService = state.activeGridType === 'service'; // true이면 근속 표를 처리한다.
        // 삼항 연산자로 대상 표의 상태와 해당 상태 변경 함수를 선택한다.
        const list: (HRM3040_R01ServiceAllowDto | HRM3040_R01PositionAllowDto)[] = isService ? state.HRM3040_R01ServiceList : state.HRM3040_R01PositionList; // 서로 다른 두 DTO를 공통 식별·신규 상태로만 비교할 대상 행
        const selected = isService ? state.selectedHRM3040_R01ServiceList : state.selectedHRM3040_R01PositionList; // 선택 식별값
        const deleted = isService ? state.deletedHRM3040_R01ServiceList : state.deletedHRM3040_R01PositionList; // 삭제 식별값
        const removeRow = isService ? state.removeHRM3040_R01ServiceList : state.removeHRM3040_R01PositionList; // 신규 제거
        const removeSelected = isService ? state.removeSelectedHRM3040_R01ServiceList : state.removeSelectedHRM3040_R01PositionList; // 선택 해제
        const addDeleted = isService ? state.addDeletedHRM3040_R01ServiceList : state.addDeletedHRM3040_R01PositionList; // D 표시 추가
        const removeDeleted = isService ? state.removeDeletedHRM3040_R01ServiceList : state.removeDeletedHRM3040_R01PositionList; // D 표시 복구
        if (deleted.length) { removeDeleted(); removeSelected(); return; }
        const selectedSet = new Set(selected); // 문자열 선택 비교용 집합
        list.filter(row => selectedSet.has(String(row.valueIndex)) && row.inserted).forEach(row => removeRow(row.valueIndex));
        addDeleted(list.filter(row => selectedSet.has(String(row.valueIndex)) && !row.inserted).map(row => String(row.valueIndex)));
        removeSelected();
    }, [stopEditing]);

    /** 두 표의 실제 변경을 분리·검증해 요청 하나로 저장한다. 실패하면 편집값을 유지한다. */
    const handleSaveClick = useCallback(async () => {
        stopEditing();
        const state = useHRM3040_R01Store.getState(); // 편집 확정 후의 최신 값
        if (!state.HRM3040_R01PastFilter.baseDt) { showWarning('먼저 조회하세요.'); return; }
        if (!hasChanges()) { showWarning('변경된 내용이 없습니다.'); return; }
        /** 전달한 타입 T의 표를 삭제 제외·신규·수정·삭제 목록으로 나누어 반환한다. 자동 빈 행 판단은 없다. */
        const splitGridRows = <T extends HRM3040_R01ServiceAllowDto | HRM3040_R01PositionAllowDto>(list: T[], deletedIds: string[]) => {
            const deletedSet = new Set(deletedIds); // 문자열 삭제 비교 집합
            const activeRows = list.filter(row => !deletedSet.has(String(row.valueIndex))); // 삭제하지 않을 업무 행
            return {
                activeRows,
                insertedList: activeRows.filter(row => row.inserted),
                modifiedList: activeRows.filter(row => row.isModified && !row.inserted),
                deleteTargetList: list.filter(row => deletedSet.has(String(row.valueIndex)) && !row.inserted),
            };
        };
        const service = splitGridRows(state.HRM3040_R01ServiceList, state.deletedHRM3040_R01ServiceList); // 근속 저장 분류
        const position = splitGridRows(state.HRM3040_R01PositionList, state.deletedHRM3040_R01PositionList); // 직책 저장 분류
        const error = validateHRM3040_R01SaveData(service.activeRows, position.activeRows); // 보이는 행의 선행 검증
        if (error) { showWarning(error); return; }
        setLoadingSpinner(true);
        try {
            const response = await patchHRM3040_R01({
                hrmServiceAllowDtoList: [...service.insertedList, ...service.modifiedList],
                hrmPositionAllowDtoList: [...position.insertedList, ...position.modifiedList],
                deletedHrmServiceAllowDtoList: service.deleteTargetList,
                deletedHrmPositionAllowDtoList: position.deleteTargetList,
            }); // 두 표를 같은 트랜잭션으로 보낼 유일한 저장 요청
            if (!response.data.successFlag) return;
            showSuccess('근속·직책수당 기준 저장이 완료되었습니다.');
            setMessage('근속·직책수당 기준 저장이 완료되었습니다.');
            await executeDataFetch('refetch', { messageMode: 'none' });
        } finally {
            setLoadingSpinner(false);
        }
    }, [stopEditing, hasChanges, setLoadingSpinner, setMessage, executeDataFetch]);

    /** 마지막 조회 조건으로 3시트 Excel 파일을 내려받는다. 조회 전에는 요청하지 않는다. */
    const handleExcelClick = useCallback(async () => {
        const past = useHRM3040_R01Store.getState().HRM3040_R01PastFilter; // 화면 입력 중 조건이 아닌 직전 조회 조건
        if (!past.baseDt) { showWarning('먼저 조회하세요.'); return; }
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_HRM3040_R01('search', past); // 마지막 조회의 3시트 파일
            if (response.data.successFlag) downloadSettingExcel({ responseData: response.data.data, fileName: `근속·직책수당 기준_${past.baseDt}-excel.xlsx` });
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner]);

    /** 변경 폐기 확인 후 전체 상태와 안내 메시지를 비운다. 재조회와 신규 행 자동 생성은 하지 않는다. */
    const initContainerData = useCallback(() => {
        stopEditing();
        confirmDiscard(() => {
            useHRM3040_R01Store.getState().initData();
            setClickable(true);
            setMessage('');
        });
    }, [stopEditing, confirmDiscard, setMessage]);

    return { clickable, setClickable, executeDataFetch, fetchData, defaultHRM3040_R01ServiceRow, defaultHRM3040_R01PositionRow,
        initContainerData, handleInsertClick, handleSaveClick, handleDeleteClick, handleExcelClick };
};
